import { useEffect, useState } from "react";
import { getTeamSettings, updateTeamSettings } from "services/team";
import Toast from "components/common/Toast";

interface ModelOption {
  value: string;
  name: string;
  credits: string;
  tag: string;
  description: string;
}

// Annotation models. Defaults/credits are scaffolded for selection; wiring is
// backend-side.
const ANNOTATION_MODELS: ModelOption[] = [
  {
    value: "gemini-flash",
    name: "Gemini Flash",
    credits: "2 Credits",
    tag: "Quick",
    description:
      "Fast and lightweight — best for quick annotations at a low credit cost.",
  },
  {
    value: "gemini-pro",
    name: "Gemini Pro",
    credits: "5 Credits",
    tag: "Smarter",
    description:
      "More capable and accurate — best when annotation quality matters most.",
  },
];

// Vijilization models.
const VIJILIZATION_MODELS: ModelOption[] = [
  {
    value: "openai",
    name: "OpenAI",
    credits: "6 Credits",
    tag: "Most capable",
    description:
      "OpenAI GPT models — strong reasoning for in-depth vijilization analysis.",
  },
  {
    value: "gemini",
    name: "Gemini",
    credits: "4 Credits",
    tag: "Balanced",
    description:
      "Google Gemini — fast multimodal analysis at a lower credit cost.",
  },
];

const ModelCard = ({
  model,
  selected,
  onSelect,
  disabled = false,
}: {
  model: ModelOption;
  selected: boolean;
  onSelect: (value: string) => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => !disabled && onSelect(model.value)}
    className={`text-left border rounded-lg p-4 transition-colors ${
      disabled ? "cursor-not-allowed opacity-60" : ""
    } ${
      selected
        ? "border-[#1751D0] bg-[#F5F8FD]"
        : disabled
          ? "border-[#D0D5DD]"
          : "border-[#D0D5DD] hover:border-[#1751D0]"
    }`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-x-2">
        <span className="text-sm font-interSemiBold text-[#101828]">
          {model.name}
        </span>
        <span className="text-xs font-interMedium text-[#1751D0] bg-[#EAF0FB] px-2 py-0.5 rounded-full">
          {model.tag}
        </span>
      </div>
      <span
        className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
          selected ? "border-[#1751D0]" : "border-[#D0D5DD]"
        }`}
      >
        {selected && (
          <span className="w-2 h-2 rounded-full bg-[#1751D0]" />
        )}
      </span>
    </div>
    <p className="text-xs font-interMedium text-[#667085] mb-1.5">
      {model.credits}
    </p>
    <p className="text-sm text-[#475467]">{model.description}</p>
  </button>
);

const getTeamId = () => {
  try {
    return JSON.parse(localStorage.getItem("selectedTeam") || "{}")
      ?.currentTeamId;
  } catch {
    return undefined;
  }
};

const getIsAdmin = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("selectedTeam") || "{}")?.currentRole ===
      "Admin"
    );
  } catch {
    return false;
  }
};

const ModelSettings = () => {
  const [annotationModel, setAnnotationModel] = useState(
    ANNOTATION_MODELS[0].value
  );
  const [vijilizationModel, setVijilizationModel] = useState(
    VIJILIZATION_MODELS[0].value
  );
  const [isSaving, setIsSaving] = useState(false);

  const isAdmin = getIsAdmin();
  // Vijilization model is locked for everyone for now. Annotation model is
  // editable for admins only.
  const vijilizationDisabled = true;
  const annotationDisabled = !isAdmin;

  // Load the team's current model selections.
  useEffect(() => {
    const teamId = getTeamId();
    if (!teamId) return;
    (async () => {
      try {
        const res: any = await getTeamSettings(teamId);
        const settings = res?.data ?? res;
        if (settings?.annotation_model)
          setAnnotationModel(settings.annotation_model);
        if (settings?.vijilization_model)
          setVijilizationModel(settings.vijilization_model);
      } catch (error) {
        console.error("Error fetching model settings:", error);
      }
    })();
  }, []);

  const handleSave = async () => {
    const teamId = getTeamId();
    if (!teamId) return;
    setIsSaving(true);
    try {
      const res: any = await updateTeamSettings({
        team_id: teamId,
        annotation_model: annotationModel,
        vijilization_model: vijilizationModel,
      });
      // Reflect the persisted values returned by the API.
      const saved = res?.data ?? res;
      if (saved?.annotation_model) setAnnotationModel(saved.annotation_model);
      if (saved?.vijilization_model)
        setVijilizationModel(saved.vijilization_model);
      Toast.success("Success", "Model settings saved");
    } catch (error) {
      console.error("Error saving model settings:", error);
      Toast.error("Error", "Failed to save model settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="mt-5">
      <p className="text-sm text-[#475467] mb-6">
        Choose the models used across your team's analyses.
      </p>

      {/* Annotation Model */}
      <div className="mb-8">
        <h5 className="text-sm text-[#344054] font-interSemiBold pb-0.5">
          Annotation Model
        </h5>
        <p className="text-sm text-[#475467] mb-3">
          Model used to generate and edit annotations.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-[720px]">
          {ANNOTATION_MODELS.map((model) => (
            <ModelCard
              key={model.value}
              model={model}
              selected={annotationModel === model.value}
              onSelect={setAnnotationModel}
              disabled={annotationDisabled}
            />
          ))}
        </div>
      </div>

      {/* Vijilization Model */}
      <div className="mb-8">
        <h5 className="text-sm text-[#344054] font-interSemiBold pb-0.5">
          Vijilization Model
        </h5>
        <p className="text-sm text-[#475467] mb-3">
          Model used to run vijilization analyses.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-[720px]">
          {VIJILIZATION_MODELS.map((model) => (
            <ModelCard
              key={model.value}
              model={model}
              selected={vijilizationModel === model.value}
              onSelect={setVijilizationModel}
              disabled={vijilizationDisabled}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={isSaving || !isAdmin}
        className={`submit-btn-blue !px-[15px] !text-sm hover:bg-[#120693] ${
          isSaving || !isAdmin ? "button-disabled" : ""
        }`}
      >
        {isSaving ? "Saving..." : "Save"}
      </button>
    </div>
  );
};

export default ModelSettings;
